import React from 'react'
import { Paper, Typography } from '@mui/material'
import { CHART_HEIGHT } from './CarbonChart'

export interface PlaceholderPanelProps {
  /** The panel's name, the same one it will carry once it has data. */
  title: string
  /** One plain sentence: what this panel needs before it can show anything. */
  message: string
  /** Box height in px. Defaults to CHART_HEIGHT so it lines up with a chart beside it. */
  height?: number
  'data-testid'?: string
}

/**
 * A grid cell the customer asked for that we cannot answer yet.
 *
 * It holds the cell's place in the arrangement and says, in one sentence, what
 * it is waiting for. It draws no chart and no number: an empty axis or a "0%"
 * would read as a measurement, and there is no measurement.
 *
 * The edges follow the rest of the kit (see `SocialChart`'s card and
 * `HeroStat`): zero radius, a right and bottom hairline, so it butts against
 * its neighbours like any other cell.
 */
export const PlaceholderPanel: React.FC<PlaceholderPanelProps> = ({
  title,
  message,
  height = CHART_HEIGHT,
  'data-testid': testId,
}) => (
  <Paper
    elevation={0}
    data-testid={testId}
    sx={{
      height,
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 0,
      borderRight: '1px solid',
      borderBottom: '1px solid',
      borderColor: 'rgba(0,0,0,0.06)',
      bgcolor: 'background.paper',
    }}
  >
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, color: 'grey.800', letterSpacing: '-0.01em' }}
    >
      {title}
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 4 }}
    >
      {message}
    </Typography>
  </Paper>
)

export default PlaceholderPanel
